import React, { useState, useEffect, useCallback } from "react";
import { Typography, Space, Table, Row, Col, Spin, Tooltip, Empty } from "antd";
import { IntentService } from "../../services/IntentService";
import { usePermissions } from "../../PermissionsProvider";
import moment from "moment";
import { IntentGroup } from "../../model_gen/intent";
import { useNavigate, Link } from "react-router-dom";
import { PlusOutlined } from "@ant-design/icons";
import { PrimaryButton } from "../shared/button/Buttons";

const IntentGroupsOverview: React.FC = () => {
    const navigate = useNavigate();
    const { apiKey, companyId } = usePermissions();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalIntentGroups, setTotalIntentGroups] = useState(0);
    const pageSize = 10;
    const [intentGroupsByPage, setIntentGroupsByPage] = useState<Record<number, IntentGroup[]>>({});

    const styles = {
        contentCard: {
            background: "#fff",
            padding: "24px",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
            border: "1px solid #f0f0f0",
        },
        tableContainer: {
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
        },
        detailsHeader: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
        },
    };

    // Add minimal CSS
    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            .intent-groups-container {
                background-color: #f5f7fa;
            }
            .interactive-row {
                cursor: pointer;
            }
        `;
        document.head.appendChild(style);
        
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    const loadIntentGroups = useCallback(() => {
        if (!apiKey || !companyId) return;

        if (!intentGroupsByPage[currentPage]) {
            IntentService.getIntentGroups(
                (currentPage - 1) * pageSize,
                pageSize,
                setIsLoading,
                apiKey
            ).subscribe(
                (response: any) => {
                    setIntentGroupsByPage(prev => ({
                        ...prev,
                        [currentPage]: response.intent_groups
                    }));
                    setTotalIntentGroups(response.total);
                    setIsLoading(false);
                },
                (error: any) => {
                    console.error("Error loading intent groups:", error);
                    setIsLoading(false);
                }
            );
        }
    }, [apiKey, companyId, currentPage, pageSize, intentGroupsByPage]);

    useEffect(() => {
        loadIntentGroups();
    }, [loadIntentGroups]);

    const columns = [
        {
            title: "Intent Name",
            dataIndex: "intent_name",
            key: "intent_name",
            width: "30%",
            render: (text: string) => (
                <Typography.Text style={{ fontWeight: 500, color: "#1890ff" }}>
                    {text}
                </Typography.Text>
            ),
            sorter: (a: IntentGroup, b: IntentGroup) => a.intent_name.localeCompare(b.intent_name),
        },
        {
            title: "Date Created",
            dataIndex: "date_created",
            key: "date_created",
            width: "25%",
            render: (text: number | null) => {
                if (!text) return <Typography.Text type="secondary">-</Typography.Text>;
                const date = moment(text);
                return (
                    <Tooltip title={date.format("MMMM D, YYYY h:mm:ss A")}>
                        <div>
                            <div style={{ fontWeight: 500 }}>{date.format("MMM D, YYYY")}</div>
                            <div style={{ color: "#8c8c8c", fontSize: "12px" }}>{date.format("h:mm A")}</div>
                        </div>
                    </Tooltip>
                );
            },
            sorter: (a: IntentGroup, b: IntentGroup) => (a.date_created || 0) - (b.date_created || 0),
        },
        {
            title: "Last Activity",
            dataIndex: "last_activity",
            key: "last_activity",
            width: "25%",
            render: (timestamp: number | null) => {
                if (!timestamp || timestamp === 0) return <Typography.Text type="secondary">N/A</Typography.Text>;
                const date = moment(timestamp);
                return (
                    <Tooltip title={date.format("MMMM D, YYYY h:mm:ss A")}>
                        <div>
                            <div style={{ fontWeight: 500 }}>{date.format("MMM D, YYYY")}</div>
                            <div style={{ color: "#8c8c8c", fontSize: "12px" }}>{date.format("h:mm A")}</div>
                        </div>
                    </Tooltip>
                );
            },
            sorter: (a: IntentGroup, b: IntentGroup) => (a.last_activity || 0) - (b.last_activity || 0),
        },
        {
            title: "# Requests",
            dataIndex: "request_count",
            key: "request_count",
            width: "20%",
            align: "right" as const,
            render: (count: number) => (
                <Typography.Text strong style={{ color: count > 0 ? "#52c41a" : "#8c8c8c" }}>
                    {count.toLocaleString()}
                </Typography.Text>
            ),
            sorter: (a: IntentGroup, b: IntentGroup) => (a.request_count || 0) - (b.request_count || 0),
        }
    ];

    if (isLoading && (!intentGroupsByPage[currentPage] || intentGroupsByPage[currentPage].length === 0)) {
        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "calc(100vh - 200px)" }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto", minHeight: "calc(100vh - 64px)" }}>
            {/* Header */}
            <div style={{ marginBottom: "32px" }}>
                <Row align="middle">
                    <Col>
                        <Space direction="vertical" size={4}>
                            <Typography.Text type="secondary">
                                <Link to="/">Home</Link> / Intent Groups
                            </Typography.Text>
                            <Typography.Title level={2} style={{ margin: 0, fontWeight: 600 }}>
                                Intent Groups
                            </Typography.Title>
                            <Typography.Text type="secondary" style={{ fontSize: "14px" }}>
                                Manage your intent groups across applications
                            </Typography.Text>
                        </Space>
                    </Col>
                </Row>
            </div>

            {/* Main Content */}
            <div style={styles.contentCard}>
                <div style={styles.detailsHeader}>
                    <Typography.Title level={5} style={{ margin: 0, fontWeight: 600 }}>
                        Intent Groups
                    </Typography.Title>
                </div>
                <div style={styles.tableContainer}>
                    <Table
                        rowKey={(record) => record.id?.toString() || record.intent_name}
                        columns={columns}
                        dataSource={intentGroupsByPage[currentPage] || []}
                        loading={isLoading}
                        rowClassName={() => "interactive-row"}
                        pagination={{
                            current: currentPage,
                            pageSize: pageSize,
                            total: totalIntentGroups,
                            showSizeChanger: false,
                            showTotal: (total) => `Total ${total} items`,
                        }}
                        onChange={(pagination) => {
                            if (pagination.current) {
                                setCurrentPage(pagination.current);
                            }
                        }}
                        onRow={(record) => ({
                            onClick: () => navigate(`/intent-group/${record.id}`)
                        })}
                        locale={{
                            emptyText: (
                                <Empty 
                                    description={
                                        <div>
                                            <Typography.Text strong style={{ display: "block", marginBottom: "8px" }}>
                                                No intent groups found
                                            </Typography.Text>
                                            <Typography.Text type="secondary">
                                                Create a new intent group to get started.
                                            </Typography.Text>
                                        </div>
                                    } 
                                    image={Empty.PRESENTED_IMAGE_SIMPLE} 
                                />
                            )
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default IntentGroupsOverview;